import {
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Autocomplete,
} from "@mui/material";
import { Box } from "@mui/system";
import { useFormik } from "formik";
import React, { useState } from "react";
import * as yup from "yup";
import {
  StyledSelect,
  StyledTextField,
} from "../../../customComponents/styled/styledInputs";
import { StyledLoadingButton } from "../../../customComponents/styled/styledButtons";
import {
  useAddTemplateToSubSectionMutation,
  useCreateSubSectionMutation,
  useGetSectionsPaginatedQuery,
} from "./sectionsApiSlice";
import toast from "../../../../helpers/toast";
import { useGetTemplatesQuery } from "./sectionsApiSlice";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../../customComponents/styled/styledTable";
import DeleteIcon from "@mui/icons-material/Delete";
import PreviewIcon from "@mui/icons-material/Preview";

const subSectionFormValidation = yup.object({
  name: yup.string().required("Required"),
  report_type: yup.string().required("Required"),
  section_id: yup.string().required("Required"),
});

function AddSubsectionForm({ focusedSection }) {
  const [createSubsection, { isLoading: creatingSubsection }] =
    useCreateSubSectionMutation();
  const [sectionSearch, setSectionSearch] = useState("");
  const { data: sectionData } = useGetSectionsPaginatedQuery({ page: 1, per_page: 10, q: sectionSearch });
  const sections = sectionData?.sections ?? [];
  const { data: templates, isLoading: templatesLoading } =
    useGetTemplatesQuery("all");

  const [addTemplateToSubSection, { isLoading: isAddingTemplateToSubSection }] =
    useAddTemplateToSubSectionMutation();

  const [newTemplateIds, setNewTemplateIds] = useState([]);

  const subSectionForm = useFormik({
    initialValues: {
      name: "",
      report_type: "",
      section_id: focusedSection?.id || "",
    },
    enableReinitialize: true,
    validationSchema: subSectionFormValidation,
    onSubmit: async (values) => {
      try {
        const response = await createSubsection(values).unwrap();
        if (response) {
          toast.success("Subsection created successfully");
          subSectionForm.resetForm();
          newTemplateIds.forEach(async (templateId) => {
            await addTemplateToSubSection({
              subsection_id: response.id,
              template_id: templateId,
            }).unwrap();
          });
          toast.success("Template added successfully");
          setNewTemplateIds([]); //reset template table
        } else {
          toast.error("Subsection creation failed");
        }
      } catch (error) {
        toast.error(error?.message || "Something went wrong");
      }
    },
  });


  return (
    <>
      <Box className="paperHeader">
        <Typography variant="formHeading">Setup Sub-Sections</Typography>
      </Box>
      <Box className="paperBody">
        <form onSubmit={subSectionForm.handleSubmit}>
          <Grid container columnSpacing={1} rowSpacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography variant="formSubHeading">Section</Typography>
                <Autocomplete
                  disabled={focusedSection !== null}
                  options={sections}
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={sections.find(s => s.id === subSectionForm.values.section_id) || null}
                  onChange={(_, value) => subSectionForm.setFieldValue("section_id", value?.id || "")}
                  onInputChange={(_, value, reason) => { if (reason === "input") setSectionSearch(value); }}
                  filterOptions={(x) => x}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      placeholder="Search section..."
                      error={subSectionForm.touched.section_id && Boolean(subSectionForm.errors.section_id)}
                    />
                  )}
                />
                <FormHelperText variant="error">
                  {subSectionForm.touched.section_id &&
                    subSectionForm.errors.section_id}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl error fullWidth>
                <Typography variant="formSubHeading">
                  Sub-Section Name
                </Typography>
                <StyledTextField
                  variant="outlined"
                  id="name"
                  name="name"
                  value={subSectionForm.values.name}
                  onChange={subSectionForm.handleChange}
                  error={
                    subSectionForm.touched.name &&
                    Boolean(subSectionForm.errors.name)
                  }
                />
                <FormHelperText variant="error">
                  {subSectionForm.touched.name && subSectionForm.errors.name}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography variant="formSubHeading">Report Type</Typography>
                <StyledSelect
                  displayEmpty
                  id="report_type"
                  name="report_type"
                  value={subSectionForm.values.report_type}
                  onChange={subSectionForm.handleChange}
                  error={
                    subSectionForm.touched.report_type &&
                    Boolean(subSectionForm.errors.report_type)
                  }
                >
                  <MenuItem value="" disabled>
                    <em>select report type</em>
                  </MenuItem>
                  <MenuItem value="score">Score Based</MenuItem>
                  <MenuItem value="skill">Comment/Skill Based</MenuItem>
                </StyledSelect>
                <FormHelperText variant="error">
                  {subSectionForm.touched.report_type &&
                    subSectionForm.errors.report_type}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography variant="formSubHeading">Templates</Typography>
                <StyledSelect
                  id="reportTemplate"
                  name="reportTemplate"
                  value={subSectionForm.values.reportTemplate}
                  // onChange={subSectionForm.handleChange}
                  onChange={(e) => {
                    const newTemplateId = e.target.value;
                    //add template id to template array
                    if (!newTemplateIds?.includes(newTemplateId)) {
                      setNewTemplateIds((prev) => {
                        return [...prev, newTemplateId];
                      });
                    } else {
                      toast.info("Template is already selected");
                    }
                  }}
                  error={
                    subSectionForm.touched.reportTemplate &&
                    Boolean(subSectionForm.errors.reportTemplate)
                  }
                >
                  <MenuItem value="">
                    <em>Select a Template</em>
                  </MenuItem>
                  {templates?.map((template) => (
                    <MenuItem value={template.id} key={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </StyledSelect>
                <FormHelperText variant="error">
                  {subSectionForm.touched.reportTemplate &&
                    subSectionForm.errors.reportTemplate}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TableContainer sx={{ pb: 4 }}>
                <Table sx={{ width: "100%", boxShadow: "none" }}>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Template </StyledTableCell>
                      <StyledTableCell>Preview </StyledTableCell>
                      <StyledTableCell align="right">delete</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {newTemplateIds?.map((templateId) => (
                      <StyledTableRow key={templateId}>
                        <StyledTableCell component="th" scope="section">
                          {
                            templates.find(
                              (template) => template.id === templateId
                            )?.name
                          }
                        </StyledTableCell>
                        <StyledTableCell component="th" scope="section">
                          <PreviewIcon
                            className="tableCellLink"
                            onClick={() => {
                              window.open(
                                templates.find(
                                  (template) => template.id === templateId
                                )?.image_url,
                                "_blank"
                              );
                            }}
                          />
                        </StyledTableCell>

                        <StyledTableCell align="right">
                          <DeleteIcon
                            className="tableCellLink"
                            onClick={() => {
                              const filteredTemplates = newTemplateIds?.filter(
                                (template) => template !== templateId
                              );
                              setNewTemplateIds(filteredTemplates);
                            }}
                          />
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12} textAlign={"right"}>
              <StyledLoadingButton loading={creatingSubsection} type="submit">
                submit
              </StyledLoadingButton>
            </Grid>
          </Grid>
        </form>
      </Box>
    </>
  );
}

export default AddSubsectionForm;
